import React from 'react';
import styled from '@emotion/styled';
import { agentKindTag } from '../../chat-agent/agentKind';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LightMermaid from './LightMermaid';
import SplitBars from '../../chat-agent/components/SplitBars';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/** The history of a thread artifact as a timeline: question, turns with their sources and cost, the ordering step, the report step, the summary. */
const HUES = { k2: 'hsl(238 48% 57%)', k3: 'hsl(285 40% 40%)' };
const Circle = styled.span`
  display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; color: #fff; flex-shrink: 0;
  width: ${(p) => p.size}px; height: ${(p) => p.size}px; font-size: ${(p) => Math.round(p.size * 0.38)}px; font-weight: 700;
  background: ${(p) => p.bg}; border: 2px solid var(--color-grey-6, #f7f7f7);
  img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
`;
export function Avatar({ slug = '?', avatarUrl, size = 28 }) {
  const bg = HUES[slug] || 'var(--color-evergreen)';
  return <Circle size={size} bg={bg} title={slug}>{avatarUrl ? <img src={avatarUrl} alt={slug} /> : String(slug).toUpperCase()}</Circle>;
}

const fmt = (n, lang) => new Intl.NumberFormat(lang === 'en' ? 'en-GB' : lang === 'sv' ? 'sv-SE' : 'fi-FI').format(n || 0);
export function usageText(u, t, lang) {
  if (!u) return '';
  const tokens = (u.input || 0) + (u.output || 0) + (u.cacheRead || 0) + (u.cacheCreation || 0);
  const parts = [];
  if (u.calls > 1) parts.push(t('chatAgent.usage.calls', { n: u.calls }));
  parts.push(t('chatAgent.usage.tokens', { n: fmt(tokens, lang) }));
  if (u.costUsd != null) parts.push(t('chatAgent.usage.cost', { usd: (Math.round(u.costUsd * 1000) / 1000).toFixed(3) }));
  return parts.join(' · ');
}

/** [n] markers link to the source list; mermaid fences render; kuutio-split fences draw as split bars (SplitProvider on the page gives the sources and the light tone). */
export const mdComponents = {
  code({ className, children, ...props }) {
    if (/language-mermaid/.test(className || '')) return <LightMermaid chart={String(children).replace(/\n$/, '')} />;
    if (/language-kuutio-split/.test(className || '')) return <SplitBars body={String(children)} />;
    return <code className={className} {...props}>{children}</code>;
  },
  pre({ children }) {
    const child = React.Children.toArray(children)[0];
    if (/language-(mermaid|kuutio-split)/.test(child?.props?.className || '')) return child;
    return <pre>{children}</pre>;
  },
  p({ children }) { return <p>{linkRefs(children)}</p>; },
  li({ children }) { return <li>{linkRefs(children)}</li>; },
  // The summary's conclusions are bold, and a [n] inside <strong> was a string child of
  // strong, not of p, so it stayed plain text (27.10.2026).
  strong({ children }) { return <strong>{linkRefs(children)}</strong>; },
  em({ children }) { return <em>{linkRefs(children)}</em>; },
};
function linkRefs(children) {
  return React.Children.map(children, (c) => {
    if (typeof c !== 'string') return c;
    const parts = c.split(/(\[\d{1,3}(?:\s*[,–-]\s*\d{1,3})*\])/g);
    return parts.map((p, i) => (/^\[\d/.test(p) ? <a key={i} className="ref" href={`#src-${p.replace(/\D/g, ' ').trim().split(' ')[0]}`}>{p}</a> : p));
  });
}

const Timeline = styled.div` border-left: 2px solid var(--color-grey-4, #d9d9d9); margin: 12px 0 0 10px; padding-left: 22px; display: flex; flex-direction: column; gap: 18px; `;
const Step = styled.div`
  position: relative;
  &::before { content: ""; position: absolute; left: -29px; top: 8px; width: 12px; height: 12px; border-radius: 50%; background: ${(p) => (p.turn ? 'var(--color-primary)' : 'var(--color-grey-4, #d9d9d9)')}; }
`;
const Head = styled.div` display: flex; align-items: center; gap: 10px; margin-bottom: 6px; flex-wrap: wrap; .name { font-weight: 700; } .label { font-size: 12.5px; color: var(--color-grey-2, #636363); } .usage { font-size: 11.5px; color: var(--color-grey-2, #636363); font-variant-numeric: tabular-nums; } `;
const Bubble = styled.div`
  background: ${(p) => (p.user ? 'var(--color-blueberry-light)' : '#fff')}; border: 1px solid ${(p) => (p.user ? 'transparent' : 'var(--color-grey-4, #d9d9d9)')};
  border-radius: 10px; padding: 12px 16px; p { margin: 0 0 8px; } ul, ol { padding-left: 20px; } a.ref { font-size: .85em; text-decoration: none; color: var(--color-primary); background: var(--color-blueberry-light); padding: 0 4px; border-radius: 4px; }
  h4 { font-size: 12px; text-transform: uppercase; letter-spacing: .06em; color: var(--color-grey-2, #636363); margin: 12px 0 4px; }
  .who { font-size: .8em; color: var(--color-primary); margin-left: 4px; } .muted { color: var(--color-grey-2, #636363); }
`;
const Srcs = styled.details` summary { cursor: pointer; font-size: 13px; color: var(--color-grey-2, #636363); margin-top: 6px; } ul { font-size: 13px; color: var(--color-grey-2, #636363); padding-left: 18px; margin: 4px 0 0; } `;

function Ordering({ kind, result }) {
  const { t } = useTranslation();
  if (!result) return <p className="muted">{t('artifact.noOrdering')}</p>;
  const ev = (xs) => (xs?.length ? <span className="muted"> ({xs.join(', ')})</span> : null);
  if (kind === 'deep_dive') {
    return (
      <>
        <h4>{t('artifact.findings')}</h4>
        <ul>{(result.findings || []).map((f, i) => <li key={i}><span className="muted">[{f.confidence}]</span> {f.finding} <span className="who">{(f.by || []).join(', ')}</span>{ev(f.evidence)}</li>)}</ul>
        <h4>{t('artifact.tensions')}</h4>
        {(result.tensions || []).map((d, i) => <div key={i}><p><strong>{d.point}</strong></p><ul>{(d.positions || []).map((p, j) => <li key={j}><span className="who">{p.agent}</span> {p.position}{ev(p.evidence)}</li>)}</ul></div>)}
        <h4>{t('artifact.gaps')}</h4><ul>{(result.gaps || []).map((g, i) => <li key={i}>{g}</li>)}</ul>
        <h4>{t('artifact.implications')}</h4><ul>{(result.implications || []).map((g, i) => <li key={i}>{g}</li>)}</ul>
      </>
    );
  }
  return (
    <>
      <h4>{t('artifact.agreed')}</h4>
      <ul>{(result.agreed || []).map((a, i) => <li key={i}>{a.point} <span className="who">{(a.by || []).join(', ')}</span>{ev(a.evidence)}</li>)}</ul>
      <h4>{t('artifact.disputed')}</h4>
      {(result.disputed || []).map((d, i) => <div key={i}><p><strong>{d.point}</strong></p><ul>{(d.positions || []).map((p, j) => <li key={j}><span className="who">{p.agent}</span> {p.position}{ev(p.evidence)}</li>)}</ul></div>)}
      <h4>{t('artifact.open')}</h4><ul>{(result.open || []).map((o, i) => <li key={i}>{o}</li>)}</ul>
      {result.converged && <p className="muted">{t('chatAgent.thread.converged')}</p>}
    </>
  );
}

export default function ArtifactHistory({ snapshot, when }) {
  const { t, language } = useTranslation();
  const th = snapshot.thread || {};
  const steps = th.usage?.steps || [];
  const orderStep = steps.find((s) => s.step === 'suodatin' || s.step === 'synteesi');
  const reportStep = steps.find((s) => s.step === 'raportti');
  return (
    <Timeline>
      <Step><Head><span className="name">{t('artifact.asker')}</span><span className="label">{when(snapshot.question?.createdAt)}</span></Head><Bubble user>{snapshot.question?.text}</Bubble></Step>
      {(snapshot.turns || []).map((turn, i) => {
        const sp = turn.speaker || {};
        return (
          <Step turn key={i}>
            <Head><Avatar slug={sp.slug} avatarUrl={sp.avatarUrl} size={30} /><span className="name">{sp.name}</span><span className="label">{sp.label}{agentKindTag(sp.kind) ? ` · ${t(agentKindTag(sp.kind))}` : ''}</span>{sp.usage && <span className="usage">{usageText(sp.usage, t, language)}</span>}</Head>
            <Bubble><ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{turn.text}</ReactMarkdown></Bubble>
            {turn.sources?.length > 0 && (
              <Srcs><summary>{t('artifact.turnSources', { n: turn.sources.length })}</summary>
                <ul>{turn.sources.map((s, j) => <li key={j}><a className="ref" href={`#src-${s.num}`}>[{s.num}]</a> {s.citation}</li>)}</ul></Srcs>
            )}
          </Step>
        );
      })}
      <Step>
        <Head><span className="name">{t(th.kind === 'deep_dive' ? 'artifact.synthesisStep' : 'artifact.filterStep')}</span>{orderStep && <span className="usage">{usageText({ ...orderStep, calls: 1 }, t, language)}</span>}</Head>
        <Bubble><Ordering kind={th.kind} result={th.result} /></Bubble>
      </Step>
      {th.report && (
        <Step><Head><span className="name">{t('artifact.reportStep')}</span>{reportStep && <span className="usage">{usageText({ ...reportStep, calls: 1 }, t, language)}</span>}</Head><Bubble className="muted">{t('artifact.reportStepNote')}</Bubble></Step>
      )}
      {snapshot.summary && <Step><Head><span className="name">{t('artifact.assistant')}</span><span className="label">{t('artifact.summaryStep')}</span></Head><Bubble className="muted">{t('artifact.summaryStepNote')}</Bubble></Step>}
    </Timeline>
  );
}
